import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * Theme Context for AskAira Platform
 * Supports Dark Mode and Light Mode with premium aesthetics
 */

export const THEMES = {
  dark: {
    id: 'dark',
    name: 'Dark Mode',
    description: 'Luxury dark theme with cinematic aesthetics',
    icon: 'moon',
    mode: 'dark',
    colors: {
      // Core backgrounds
      bgPrimary: '#030014',
      bgSecondary: '#0a0a1a',
      bgTertiary: '#121228',
      bgCard: 'rgba(255, 255, 255, 0.03)',
      bgCardHover: 'rgba(255, 255, 255, 0.07)',
      bgInput: 'rgba(255, 255, 255, 0.05)',
      bgGlass: 'rgba(255, 255, 255, 0.05)',
      bgGlassHover: 'rgba(255, 255, 255, 0.08)',
      bgOverlay: 'rgba(0, 0, 0, 0.6)',
      
      // Text colors
      textPrimary: '#ffffff',
      textSecondary: 'rgba(255, 255, 255, 0.7)',
      textTertiary: 'rgba(255, 255, 255, 0.5)',
      textMuted: 'rgba(255, 255, 255, 0.4)',
      
      // Brand colors
      primary: '#FF007F',
      primaryLight: 'rgba(255, 0, 127, 0.2)',
      secondary: '#7B2FBE',
      secondaryLight: 'rgba(123, 47, 190, 0.2)',
      accent: '#00E5FF',
      accentLight: 'rgba(0, 229, 255, 0.2)',
      gold: '#FFD700',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      
      // Travel brand colors
      travelPrimary: '#FF007F',
      travelSecondary: '#00E5FF',
      travelGold: '#FFD700',
      
      // Borders
      border: 'rgba(255, 255, 255, 0.1)',
      borderHover: 'rgba(255, 255, 255, 0.2)',
      borderAccent: 'rgba(255, 0, 127, 0.5)',
      
      // Gradients
      gradientPrimary: 'linear-gradient(135deg, #FF007F 0%, #7B2FBE 100%)',
      gradientAccent: 'linear-gradient(135deg, #00E5FF 0%, #FF007F 100%)',
      gradientGold: 'linear-gradient(135deg, #FFD700 0%, #FF6B35 100%)',
      gradientHero: 'linear-gradient(180deg, rgba(3,0,20,0.8) 0%, rgba(3,0,20,0.4) 50%, rgba(3,0,20,1) 100%)',
      
      // Effects
      shadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
      shadowGlow: '0 0 30px rgba(255, 0, 127, 0.3)',
      blur: '16px',
    }
  },
  light: {
    id: 'light',
    name: 'Light Mode',
    description: 'Premium light theme inspired by Apple & Notion',
    icon: 'sun',
    mode: 'light',
    colors: {
      // Core backgrounds - Warm, premium whites
      bgPrimary: '#FAFAFA',
      bgSecondary: '#F5F5F7',
      bgTertiary: '#EBEBED',
      bgCard: 'rgba(255, 255, 255, 0.8)',
      bgCardHover: 'rgba(255, 255, 255, 0.95)',
      bgInput: 'rgba(0, 0, 0, 0.03)',
      bgGlass: 'rgba(255, 255, 255, 0.7)',
      bgGlassHover: 'rgba(255, 255, 255, 0.9)',
      bgOverlay: 'rgba(255, 255, 255, 0.8)',
      
      // Text colors - Deep, rich blacks
      textPrimary: '#1a1a2e',
      textSecondary: '#4a4a5e',
      textTertiary: '#6b6b7e',
      textMuted: '#9ca3af',
      
      // Brand colors - Slightly softer for light mode
      primary: '#E6006E',
      primaryLight: 'rgba(230, 0, 110, 0.1)',
      secondary: '#6B21A8',
      secondaryLight: 'rgba(107, 33, 168, 0.1)',
      accent: '#0891B2',
      accentLight: 'rgba(8, 145, 178, 0.1)',
      gold: '#D97706',
      success: '#059669',
      warning: '#D97706',
      error: '#DC2626',
      
      // Travel brand colors
      travelPrimary: '#E6006E',
      travelSecondary: '#0891B2',
      travelGold: '#D97706',
      
      // Borders - Subtle and elegant
      border: 'rgba(0, 0, 0, 0.08)',
      borderHover: 'rgba(0, 0, 0, 0.15)',
      borderAccent: 'rgba(230, 0, 110, 0.3)',
      
      // Gradients
      gradientPrimary: 'linear-gradient(135deg, #E6006E 0%, #6B21A8 100%)',
      gradientAccent: 'linear-gradient(135deg, #0891B2 0%, #E6006E 100%)',
      gradientGold: 'linear-gradient(135deg, #D97706 0%, #EA580C 100%)',
      gradientHero: 'linear-gradient(180deg, rgba(250,250,250,0.9) 0%, rgba(250,250,250,0.3) 50%, rgba(250,250,250,1) 100%)',
      
      // Effects - Softer shadows
      shadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
      shadowGlow: '0 0 30px rgba(230, 0, 110, 0.15)',
      blur: '20px',
    }
  }
};

const ThemeContext = createContext();

// Apply CSS variables to document
const applyTheme = (theme) => {
  const root = document.documentElement;
  const colors = THEMES[theme].colors;
  
  // Core backgrounds
  root.style.setProperty('--bg-primary', colors.bgPrimary);
  root.style.setProperty('--bg-secondary', colors.bgSecondary);
  root.style.setProperty('--bg-tertiary', colors.bgTertiary);
  root.style.setProperty('--bg-card', colors.bgCard);
  root.style.setProperty('--bg-card-hover', colors.bgCardHover);
  root.style.setProperty('--bg-input', colors.bgInput);
  root.style.setProperty('--bg-glass', colors.bgGlass);
  root.style.setProperty('--bg-glass-hover', colors.bgGlassHover);
  root.style.setProperty('--bg-overlay', colors.bgOverlay);
  
  // Text colors
  root.style.setProperty('--text-primary', colors.textPrimary);
  root.style.setProperty('--text-secondary', colors.textSecondary);
  root.style.setProperty('--text-tertiary', colors.textTertiary);
  root.style.setProperty('--text-muted', colors.textMuted);
  
  // Brand colors
  root.style.setProperty('--color-primary', colors.primary);
  root.style.setProperty('--color-primary-light', colors.primaryLight);
  root.style.setProperty('--color-secondary', colors.secondary);
  root.style.setProperty('--color-secondary-light', colors.secondaryLight);
  root.style.setProperty('--color-accent', colors.accent);
  root.style.setProperty('--color-accent-light', colors.accentLight);
  root.style.setProperty('--color-gold', colors.gold);
  root.style.setProperty('--color-success', colors.success);
  root.style.setProperty('--color-warning', colors.warning);
  root.style.setProperty('--color-error', colors.error);
  
  // Travel colors
  root.style.setProperty('--travel-primary', colors.travelPrimary);
  root.style.setProperty('--travel-secondary', colors.travelSecondary);
  root.style.setProperty('--travel-gold', colors.travelGold);
  
  // Borders
  root.style.setProperty('--border-color', colors.border);
  root.style.setProperty('--border-color-hover', colors.borderHover);
  root.style.setProperty('--border-accent', colors.borderAccent);
  
  // Gradients
  root.style.setProperty('--gradient-primary', colors.gradientPrimary);
  root.style.setProperty('--gradient-accent', colors.gradientAccent);
  root.style.setProperty('--gradient-gold', colors.gradientGold);
  root.style.setProperty('--gradient-hero', colors.gradientHero);
  
  // Effects
  root.style.setProperty('--shadow', colors.shadow);
  root.style.setProperty('--shadow-glow', colors.shadowGlow);
  root.style.setProperty('--blur', colors.blur);
  
  // Add theme class to body
  document.body.classList.remove('theme-dark', 'theme-light');
  document.body.classList.add(`theme-${theme}`);
  
  // Update meta theme-color for mobile browsers
  const metaTheme = document.querySelector('meta[name="theme-color"]');
  if (metaTheme) {
    metaTheme.setAttribute('content', colors.bgPrimary);
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState('dark');
  
  // Load saved theme on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('aira-theme');
    if (savedTheme && THEMES[savedTheme]) {
      setThemeState(savedTheme);
      applyTheme(savedTheme);
    } else {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const defaultTheme = prefersDark ? 'dark' : 'dark'; // Default to dark for our brand
      setThemeState(defaultTheme);
      applyTheme(defaultTheme);
    }
  }, []);
  
  // Apply theme when it changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);
  
  const setTheme = (newTheme) => {
    if (THEMES[newTheme]) {
      setThemeState(newTheme);
      localStorage.setItem('aira-theme', newTheme);
      applyTheme(newTheme);
    }
  };
  
  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
  };
  
  const themeConfig = THEMES[theme];
  const isDark = theme === 'dark';
  const isLight = theme === 'light';
  
  return (
    <ThemeContext.Provider value={{ 
      theme,
      themeConfig,
      setTheme,
      toggleTheme,
      isDark,
      isLight,
      colors: themeConfig.colors
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
